/**
 * Format date to readable string
 * @param date - Date to format
 */
function parseApiDate(dateString: string): Date {
  if (!dateString) return new Date();

  // If backend did not send timezone, assume it's UTC and add "Z"
  const hasTimezone = /Z$|[+-]\d{2}:\d{2}$/.test(dateString);
  const safe = hasTimezone ? dateString : `${dateString}Z`;

  return new Date(safe);
}

 export  const formatDate = (date: Date | string): string => {

  
  const d =
    typeof date === 'string'
      ? parseApiDate(date)
      : date;
  
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;

  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
};

/**
 * Format time to readable string
 * @param date - Date to format
 */
export const formatTime = (date: Date | string): string => {
  const d = typeof date === 'string' ? parseApiDate(date) : date;

  return d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

/**
 * Format a full date and time in the user's local timezone (e.g. "Aug 20, 2026, 9:06 PM")
 * @param date - Date to format
 */
export const formatDateTime = (date: Date | string): string => {
  const d = typeof date === 'string' ? parseApiDate(date) : date;

  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

/**
 * Calculate age from birthdate
 * @param birthdate - Birthdate
 * @returns age in years, or null if the date is invalid/unset
 */
export const calculateAge = (birthdate: Date | string): number | null => {
  const birth = typeof birthdate === 'string' ? new Date(birthdate) : birthdate;

  if (!birth || Number.isNaN(birth.getTime())) return null;

  const year = birth.getFullYear();
  if (year < 1900) return null; 

  const today = new Date();
  if (birth > today) return null; 

  let age = today.getFullYear() - year;
  const monthDiff = today.getMonth() - birth.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }

  if (age < 0 || age > 130) return null;
  
  return age;
};

/**
 * Calculate distance between two locations (simplified)
 * @param lat1 - Latitude 1
 * @param lon1 - Longitude 1
 * @param lat2 - Latitude 2
 * @param lon2 - Longitude 2
 */
export const calculateDistance = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number => {
  const R = 3959; 
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;
  
  return Math.round(distance);
};

const toRad = (value: number): number => {
  return (value * Math.PI) / 180;
};

/**
 * Truncate text with ellipsis
 * @param text - Text to truncate
 * @param maxLength - Maximum length
 */
export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim() + '...';
};

/**
 * Debounce function
 * @param func - Function to debounce
 * @param wait - Wait time in milliseconds
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout | null = null;

  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

/**
 * Validate email format
 * @param email - Email to validate
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate phone number format (US)
 * @param phone - Phone number to validate
 */
export const isValidPhone = (phone: string): boolean => {
  const phoneRegex = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;
  return phoneRegex.test(phone);
};

/**
 * Format phone number
 * @param phone - Phone number to format
 */
export const formatPhone = (phone: string): string => {
  const cleaned = phone.replace(/\D/g, '');
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  
  if (match) {
    return '(' + match[1] + ') ' + match[2] + '-' + match[3];
  }
  
  return phone;
};

/**
 * Get initials from name
 * @param firstName - First name
 * @param lastName - Last name
 */
export const getInitials = (firstName: string, lastName?: string): string => {
  const first = firstName?.charAt(0)?.toUpperCase() || '';
  const last = lastName?.charAt(0)?.toUpperCase() || '';
  return first + last;
};

export const generateId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Check if user is online (last active within 5 minutes)
 * @param lastActive - Last active timestamp
 */
export const isUserOnline = (lastActive: Date | string): boolean => {
  const lastActiveDate = typeof lastActive === 'string' ? new Date(lastActive) : lastActive;
  const now = new Date();
  const diff = now.getTime() - lastActiveDate.getTime();
  const minutes = diff / 60000;
  
  return minutes < 5;
};

/**
 * Get color for medical specialty (for UI)
 * @param specialty - Medical specialty name
 */
export const getSpecialtyColor = (specialty: string): string => {
  const colors: Record<string, string> = {
    Cardiology: 'var(--brand-primary)',
    Neurology: '#8B5CF6',
    Pediatrics: '#F59E0B',
    Surgery: '#10B981',
    Psychiatry: '#3B82F6',
    'Emergency Medicine': 'var(--brand-hover)',
    Oncology: '#EC4899',
    default: '#6B7280',
  };
  
  return colors[specialty] || colors.default;
};

const monthNames = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'
];

export const GetUSIDateTime = (dateString?: string | null) => {
  if (!dateString) {
    return {
      date: '-',
      time: '-',
    };
  }

  const date = new Date(dateString + 'Z');

  const day = String(date.getDate()).padStart(2, '0');
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();

  const time = date.toLocaleString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });

  return {
    date: `${day}-${month}-${year}`,
    time,
  };
};

export const useUtil = () => {
  return {
    formatDate,
    formatTime,
    formatDateTime,
    calculateAge,
    calculateDistance,
    truncateText,
    debounce,
    isValidEmail,
    isValidPhone,
    formatPhone,
    getInitials,
    generateId,
    isUserOnline,
    getSpecialtyColor,
    GetUSIDateTime,
  };
};
